import React, { useState, useEffect } from 'react';
import { func } from 'prop-types';
import {
  TextFieldLabel, Select, Button, Spinner, TextField,
} from 'ui-component-library';
import * as yup from 'yup'; // for everything

import { useSelector } from 'react-redux';
import {
  FormHeader,
  ProblemInputContainer,
  SubmitButtonContainer,
  TopRowContainer,
  LoadingContainer,
} from './action-log-problem.style';
import { setLoadProblem, checkEmailServiceHealth } from '../../../../../../utils/api/load';
import {
  showSuccessMessage, showErrorMessage, logger, validate,
} from '../../../../../../utils/common';
import { createOption, formatErrorResponse } from '../../../../../../utils/mappers';
import CommentBox from '../../../comments-box';
import loadShape from '../../../../../../shapes/load';
import EmailModal from '../../../modals/email-modal/email-modal';
import { getLumperEmailBody, getLoadIdHyperlink, getProblemEmailBody } from '../../../modals/email-modal/email-templates';
import { getHtmlFromTxt, removeFirstLine, splitEmailString } from '../../../../../../utils/formatters';
import EmailErrorModal from '../../../modals/email-modal-error';
import { COMMENTS_CHARACTER_LIMIT } from '../../../../../../constants';

// Validation Schema
export const schema = yup.object().shape({
  problem: yup.string().required(),
  note: yup.string().required().max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less that ${COMMENTS_CHARACTER_LIMIT}`),
  lumperFee: yup.number().when('problem', {
    is: (val) => (val === 'Lumper Reimbursement'),
    then: yup.number().required(),
    otherwise: yup.number().optional().nullable(),
  }),
});

/**
 * Log problem action form
 * @param {object} params - Contains props for this component
 * @param {object} params.load - Load object to perform action on
 * @param {function} params.refetchLoad - Function to reload load summary in parent
 * @return {element} - The log problem form
 */
const ProblemLogAction = ({ load, refetchLoad }) => {
  // State hooks
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [lumperFee, setLumperFee] = useState(null);
  const [emailFailed, setEmailFailed] = useState(false);
  const [showProblemEmailModal, setShowProblemEmailModal] = useState(false);

  // Redux hooks
  const user = useSelector((store) => store.oidc.user);
  const metadata = useSelector((state) => state.metadata);

  // Generate lumper email content on render
  const lumperEmailModalContent = {
    copyTo: user.emailaddress,
    recipient: '',
    subject: `LUMPER ADDED #${load.billOfLadingNumber} ${load.originState}-${load.destinationState} ${load.customerName}`,
    body: getLumperEmailBody(load, comments, lumperFee),
  };

  // Generate problem email content on render
  const problemEmailModalContent = {
    copyTo: user.emailaddress,
    recipient: '',
    subject: `${selectedProblem?.value} #${load.billOfLadingNumber}`,
    body: getProblemEmailBody(load, selectedProblem?.value, comments, user.username),
  };

  /**
   * Function to clear the current state of the form
   * @return {void}
   */
  const resetState = () => {
    setSelectedProblem(null);
    setComments(null);
  };

  useEffect(() => {
    resetState();
  }, [load]);

  /**
   * Checks if the form is valid
   * @return {boolean}
   */
  const checkValid = () => {
    const payload = {
      problem: selectedProblem?.value,
      note: comments,
      lumperFee,
    };
    const valid = validate(payload, schema);
    return valid;
  };

  /**
   * Handles when the selected problem has changed
   * @param {object} event - Change event
   * @return {void}
   */
  const handleSelectedProblemChanged = (event) => {
    setSelectedProblem(event);
  };

  /**
   * Get problem options from metadata endpoint
   * @return {object[]} - Options array
   */
  const getProblemOptions = () => {
    const problems = metadata.data.problemsForStatuses[load.tlStatus];
    return problems?.filter((p) => !load.problems?.includes(p))
      .map((option) => createOption(option));
  };

  /**
   * Handles when comments change
   * @param {object} event - Change event
   * @return {void}
   */
  const handleCommentsChanged = (event) => {
    setComments(event.target.value);
  };

  /**
   * Handles when lumper fee change
   * @param {object} event - Change event
   * @return {void}
   */
  const handleLumperFeeChange = (event) => {
    setLumperFee(event.target.value);
  };

  /**
   * Set lumper fee to a float
   * @return {void}
   */
  const setLumperFeeFloat = () => {
    setLumperFee(parseFloat(lumperFee).toFixed(2));
  };

  /**
   * Handles submitting a problem that was logged and sending email
   * @param {object} emailContent - Email modal form content
   * @return {void}
   */
  const handleLogProblem = (emailContent) => {
    const payload = {
      problem: selectedProblem.value,
      note: comments,
    };
    const createEmailBody = getLoadIdHyperlink(load.loadId, load.billOfLadingNumber)
      + getHtmlFromTxt(removeFirstLine(emailContent.body));
    setLoading(true);
    setLoadProblem({
      ...payload,
      loadId: load.loadId,
      salesOrderBk: load.billOfLadingNumber,
      email: {
        to: emailContent ? splitEmailString(emailContent.recipient) : null,
        copyTo: emailContent ? splitEmailString(emailContent.copyTo) : null,
        subject: emailContent ? emailContent.subject : null,
        body: emailContent ? createEmailBody : null,
        isBodyHtml: !!emailContent,
        attachments: null,
      },
      reimbursementFee: lumperFee,
    }).then(() => {
      showSuccessMessage(`Problem logged on load #${load.billOfLadingNumber}`);
      resetState();
      const currentProblems = load.problems || [];
      refetchLoad({ problems: [...currentProblems, payload.problem] });
    }).catch((error) => {
      logger.error(formatErrorResponse(error));
      // If log a problem fails because email service is down or failed to send email
      if (error?.response?.data?.Message?.includes('email')) {
        setEmailFailed(true);
        setShowErrorModal(true);
      } else {
        showErrorMessage('Something went wrong. The service is currently unavailable. Please try again later.');
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  /**
   * Handles submit for lumper reimbursement problem
   * and other problems on a load for the action component
   * @return {void}
   */
  const handleSubmit = () => {
    const valid = checkValid();
    if (valid) {
      setLoading(true);
      // Check to see if email service is running
      checkEmailServiceHealth().then(() => {
        setShowProblemEmailModal(true);
      }).catch(() => {
        setShowErrorModal(true);
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  /**
   * Handles close for email error modal
   * @return {void}
   */
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    // If email failed but problem was logged
    if (emailFailed) {
      showSuccessMessage(`Problem logged on load #${load.billOfLadingNumber}`);
    }
  };

  /**
   * Handles Problem Email Modal submit
   * @return {void}
   */
  const handleProblemEmailModalSubmit = (emailContent) => {
    setShowProblemEmailModal(false);
    handleLogProblem(emailContent);
  };

  /**
   * Handles Email Error Modal submit
   * @return {void}
   */
  const handleErrorModalSubmit = () => {
    setShowErrorModal(false);
    handleLogProblem(false);
  };

  if (loading) {
    return (
      <div data-testid="Update Status Action">
        <FormHeader>Log a Problem</FormHeader>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </div>
    );
  }

  const options = getProblemOptions();
  const valid = checkValid();
  const problemSuccessEmailFailedText = 'The problem was successfully logged but the email did not send. Please copy the problem information below and email it to the load manager. Then, close the pop-up.';
  const emailFailedText = `Error: The email service is currently down. Please copy the problem information below and email it to the load manager.
  Once you have copied the information, confirm logging the problem.`;
  return (
    <div data-testid="Log Problem Action">
      {showProblemEmailModal && (
        <EmailModal
          showModal={showProblemEmailModal}
          title={selectedProblem.value === 'Lumper Reimbursement' ? 'Email Lumper Information' : 'Problem Email Notification'}
          textAreaHeight={selectedProblem.value === 'Lumper Reimbursement' ? '180px' : '220px'}
          emailContent={selectedProblem.value === 'Lumper Reimbursement' ? lumperEmailModalContent : problemEmailModalContent}
          closeModal={() => setShowProblemEmailModal(false)}
          onEmailSubmit={handleProblemEmailModalSubmit}
          customerBk={load?.customerBk}
        />
      )}
      <EmailErrorModal
        showModal={showErrorModal}
        title="Email Service Down"
        errorMessage={emailFailed ? problemSuccessEmailFailedText : emailFailedText}
        content={lumperEmailModalContent.body}
        cancelButtonLabel={emailFailed ? 'Close' : 'Cancel'}
        submitButtonLabel="Confirm"
        closeModal={handleCloseErrorModal}
        onEmailSubmit={handleErrorModalSubmit}
        emailFailed={emailFailed}
        height="180px"
      />
      <FormHeader>Log a Problem</FormHeader>
      <TopRowContainer>
        <ProblemInputContainer>
          <TextFieldLabel text="*Problem on Load" />
          <Select
            selectedValue={selectedProblem}
            options={options}
            placeholder="Select a problem on the current status"
            onValueChange={handleSelectedProblemChanged}
          />
        </ProblemInputContainer>
        {selectedProblem?.value === 'Lumper Reimbursement' && (
          <ProblemInputContainer>
            <TextFieldLabel text="*Lumper Fee" />
            <TextField
              name="lumperFee"
              placeholder="Enter Amount"
              value={lumperFee}
              onValueChange={handleLumperFeeChange}
              autoComplete="off"
              prefix="$"
              type="number"
              onBlur={setLumperFeeFloat}
            />
          </ProblemInputContainer>
        )}
      </TopRowContainer>
      <CommentBox
        comment={comments}
        onCommentChanged={handleCommentsChanged}
        placeholder="Include helpful details related to the problem on this load."
        characterLimit={COMMENTS_CHARACTER_LIMIT}
        required
      />
      <SubmitButtonContainer>
        <Button
          onClick={handleSubmit}
          text="Submit"
          isDisabled={!valid}
        />
      </SubmitButtonContainer>
    </div>
  );
};

ProblemLogAction.propTypes = {
  load: loadShape.isRequired,
  refetchLoad: func,
};

ProblemLogAction.defaultProps = {
  refetchLoad: () => { },
};

export default ProblemLogAction;
